import { useCallback, useState } from "react"
import { Payment, createPayment } from "@/api/payment-pages"
import { Network, Token } from "@/config/tokens"
import { Wallet } from "@/api/wallets"
import BalancesSelect from "./balances.select"
import { Transaction } from "@/api/transactions"
import PayWithBalanceConfirm from "./pay-with-balance-confirm"
import { User } from "@/api/users"
import { useWalletActions } from "@/wallet/actions"
import { Button } from "@/components/ui/button"
import Loader from "@/components/layout/loader"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { paymentFormResolver, paymentFormSchema } from "@/pay/payment.schema"
import AmountField from "@/pay/amount.field"

interface PayWithBalanceProps {
  shortId: string;
  user: User;
  wallet: Wallet;
  networks: Network[];
  token: Token;
  amount?: number;
  amountOptions?: {
    min?: number;
    max?: number;
    presetAmounts?: number[];
  };
  onDone: (payment: Payment, transaction: Transaction) => void;
  onStart: () => void;
}

export default function PayWithBalanceFlow({
  shortId,
  user,
  wallet,
  networks,
  token,
  amount,
  amountOptions,
  onDone,
  onStart,
}: PayWithBalanceProps) {
  const [payment, setPayment] = useState<Payment>()
  const [step, setStep] = useState<"network" | "paying" | "confirm" | "done">(
    "network",
  )
  const [transactionId, setTransactionId] = useState<string>()
  const { makePayment } = useWalletActions()
  const form = useForm<
    z.input<typeof paymentFormSchema>,
    undefined,
    z.output<typeof paymentFormSchema>
  >({
    resolver: paymentFormResolver(amountOptions),
    defaultValues: {
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
      amount: amount ? String(amount) : "",
    },
  })

  const onConfirmed = useCallback(
    (transaction: Transaction) => {
      onDone(payment!, transaction)
      setStep("done")
    },
    [onDone, payment],
  )

  const onSubmit = async (data: z.output<typeof paymentFormSchema>) => {
    setStep("paying")
    onStart()
    const payment = await createPayment(shortId, data)
    const { transactionId } = await makePayment(
      payment.token,
      payment.network,
      payment.amount,
      payment.address,
    )
    setTransactionId(transactionId)
    setPayment(payment)
    setStep("confirm")
  }

  if (!user) return null

  if (step === "network" && wallet) {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-8">
            {amountOptions && (
              <AmountField
                token={token}
                presetAmounts={amountOptions.presetAmounts}
              />
            )}
            <FormField
              name="network"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Network</FormLabel>
                  <BalancesSelect
                    selected={{ network: field.value, token }}
                    onSelect={field.onChange}
                    amount={Number(form.watch("amount"))}
                    token={token}
                    networks={networks}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={!form.watch("amount") || !form.watch("network")}
              data-testid="pay-now"
            >
              Pay {form.watch("amount")} {token} now
            </Button>
          </div>
        </form>
      </Form>
    )
  } else if (step === "paying") {
    return <Loader message="Paying, this can take a minute..." />
  } else if (step === "confirm" && payment) {
    return (
      <PayWithBalanceConfirm
        payment={payment}
        shortId={shortId}
        transactionId={transactionId!}
        onConfirmed={onConfirmed}
      />
    )
  }
}
